/**
 * DocsLayout — secondary right rail listing the 12 doc pages, grouped.
 * Renders inside the main <Layout>'s content area, so we get the main
 * sidebar + the document column + this rail.
 *
 * Below md breakpoint the rail collapses to a Select dropdown so the
 * document column gets the full width.
 */

import {
  Box,
  MenuItem,
  Select,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { ReactElement } from 'react';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { DOC_GROUPS, DOCS } from '@/pages/docs/nav';

const RAIL_WIDTH = 220;

function ActiveSlug(): string {
  const { pathname } = useLocation();
  const m = pathname.match(/^\/docs\/?(.+)?$/);
  return m?.[1] ?? 'index';
}

function Rail(): ReactElement {
  const active = ActiveSlug();
  return (
    <Box
      component="aside"
      aria-label="Docs navigation"
      sx={{
        width: RAIL_WIDTH,
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        alignSelf: 'flex-start',
        py: 1,
        pl: 3,
        borderLeft: '1px solid',
        borderColor: 'divider',
        maxHeight: 'calc(100vh - 16px)',
        overflowY: 'auto',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: 'text.faintest',
          mb: 1.5,
          px: 1,
        }}
      >
        ▸ docs
      </Typography>
      {DOC_GROUPS.map((group) => {
        const items = DOCS.filter((d) => d.group === group.key);
        if (items.length === 0) return null;
        return (
          <Box key={group.key} sx={{ mb: 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: 'text.faintest',
                px: 1,
                mb: 0.5,
                opacity: 0.7,
              }}
            >
              {group.label}
            </Typography>
            <Stack gap={0.25}>
              {items.map((doc) => {
                const isActive = doc.slug === active;
                const path = doc.slug === 'index' ? '/docs' : `/docs/${doc.slug}`;
                return (
                  <Box
                    key={doc.slug}
                    component={RouterLink}
                    to={path}
                    aria-current={isActive ? 'page' : undefined}
                    sx={{
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      px: 1,
                      py: 0.5,
                      borderRadius: 0.5,
                      borderLeft: '2px solid',
                      borderLeftColor: isActive ? 'primary.main' : 'transparent',
                      backgroundColor: isActive ? 'primary.soft' : 'transparent',
                      color: isActive ? 'primary.main' : 'text.disabled',
                      textShadow: isActive ? '0 0 6px rgba(217, 119, 87, 0.55)' : 'none',
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: 12,
                      transition: 'color 120ms ease, background-color 120ms ease',
                      '&:hover': {
                        color: 'text.primary',
                        backgroundColor: isActive ? 'primary.soft' : 'rgba(255,255,255,0.02)',
                      },
                    }}
                  >
                    <Box component="span" sx={{ minWidth: 12, opacity: 0.85 }}>
                      {isActive ? '▸' : '·'}
                    </Box>
                    <Box component="span" sx={{ ml: 0.5 }}>
                      {doc.title}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
}

function MobileSelect(): ReactElement {
  const navigate = useNavigate();
  const active = ActiveSlug();
  return (
    <Select
      size="small"
      value={active}
      onChange={(e) => {
        const slug = e.target.value;
        navigate(slug === 'index' ? '/docs' : `/docs/${slug}`);
      }}
      sx={{
        mb: 2,
        width: '100%',
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: 13,
      }}
    >
      {DOC_GROUPS.flatMap((group) => [
        <MenuItem key={`label-${group.key}`} disabled sx={{ opacity: 0.6, fontSize: 11 }}>
          ── {group.label} ──
        </MenuItem>,
        ...DOCS.filter((d) => d.group === group.key).map((doc) => (
          <MenuItem key={doc.slug} value={doc.slug} sx={{ fontSize: 13, fontFamily: 'inherit' }}>
            {doc.title}
          </MenuItem>
        )),
      ])}
    </Select>
  );
}

export function DocsLayout(): ReactElement {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
      <Box sx={{ flex: 1, minWidth: 0, maxWidth: 1024 }}>
        {!isDesktop && <MobileSelect />}
        <Outlet />
      </Box>
      {isDesktop && <Rail />}
    </Box>
  );
}
